import * as THREE from 'three'
import { computeScalarHistogram } from '@/plugins/gsplat/lod/scalarHistogram'
import type { GSplatWorldSplat } from '@/plugins/gsplat/unified/world/GSplatWorldState'
import type { AssetId } from '@/shared/types/core'
import type {
  ScalarFilterType,
  ScalarHistogramOptions,
  ScalarHistogramResult,
} from '@/shared/types/gsplat'

/** ITU-R BT.601 luma 权重，与 WorkBuffer copy pass 的 value 标量一致。 */
const LUMA_R = 0.299
const LUMA_G = 0.587
const LUMA_B = 0.114

/** Unified 直方图采集所需的 World 快照；只读借用，不持有生命周期。 */
export interface UnifiedScalarHistogramSource {
  readonly splats: readonly GSplatWorldSplat[]
  readonly version: number
}

/**
 * 基于 Resource 保留的格式原生 CPU 数据计算标量直方图。
 *
 * 标量定义与 WorkBuffer copy pass 的渲染过滤一致：
 * - volume = sx * sy * sz（实际半径，非 log scale）
 * - surface-area = sx² + sy² + sz²
 * - value = ITU-R BT.601 luma(readBaseColor)
 *
 * 环境 splat 与空 active 区间不参与统计（对齐 2.1 的直方图环境豁免）。
 * 无可用 CPU 读取器时返回 null，而不是投影出错误的零值直方图。
 */
export function computeUnifiedScalarHistogram(input: {
  readonly source: UnifiedScalarHistogramSource
  readonly assetId: AssetId | undefined
  readonly options: ScalarHistogramOptions
}): ScalarHistogramResult | null {
  const { source, assetId, options } = input
  if (assetId === undefined) return null

  const splats = source.splats.filter(
    (splat) => splat.assetId === assetId && splat.isEnvironment !== true && splat.activeSplats > 0
  )
  if (splats.length === 0) return null

  const values: number[] = []
  for (const splat of splats) {
    collectSplatScalars(splat, options.type, values)
  }
  if (values.length === 0) return null

  return computeScalarHistogram({
    values: Float32Array.from(values),
    ranges: [{ baseIndex: 0, count: values.length }],
    textureVersion: source.version,
    binCount: options.binCount,
    scale: options.scale,
    percentileRange: options.percentileRange,
  })
}

function collectSplatScalars(splat: GSplatWorldSplat, type: ScalarFilterType, out: number[]): void {
  const resource = splat.resource
  const readShape = resource.readSplatShape?.bind(resource)
  const readColor = resource.readSplatColor?.bind(resource)
  const needsScale = type === 'volume' || type === 'surface-area'
  if (needsScale && !readShape) return
  if (type === 'value' && !readColor) return

  const position = new THREE.Vector3()
  const rotation = new THREE.Quaternion()
  const radii = new THREE.Vector3()
  const color = new THREE.Vector4()
  const intervals = splat.intervals.length > 0 ? splat.intervals : [0, splat.activeSplats]

  for (let pairIndex = 0; pairIndex < intervals.length; pairIndex += 2) {
    const start = intervals[pairIndex]
    const end = intervals[pairIndex + 1]
    for (let index = start; index < end; index++) {
      const scalar =
        type === 'value'
          ? readColorScalar(readColor!, index, color)
          : readScaleScalar(readShape!, type, index, position, rotation, radii)
      if (scalar !== null && Number.isFinite(scalar)) out.push(scalar)
    }
  }
}

function readScaleScalar(
  readShape: NonNullable<GSplatWorldSplat['resource']['readSplatShape']>,
  type: ScalarFilterType,
  index: number,
  position: THREE.Vector3,
  rotation: THREE.Quaternion,
  radii: THREE.Vector3
): number | null {
  if (!readShape(index, position, rotation, radii)) return null
  if (type === 'volume') return radii.x * radii.y * radii.z
  return radii.x * radii.x + radii.y * radii.y + radii.z * radii.z
}

function readColorScalar(
  readColor: NonNullable<GSplatWorldSplat['resource']['readSplatColor']>,
  index: number,
  color: THREE.Vector4
): number | null {
  if (!readColor(index, color)) return null
  return LUMA_R * color.x + LUMA_G * color.y + LUMA_B * color.z
}
